/**
 * Turn a failed jobq-api response into a message a human can act on.
 *
 * WHY THIS EXISTS — the bug it prevents (Mike, 2026-09-01):
 * Saving a material returned a red banner containing the literal text
 *
 *   {"detail":"An internal server error occurred."}
 *
 * Two separate failures stacked up to produce that:
 *
 *   1. The page threw `new Error(await res.text())`, so the RAW HTTP body —
 *      JSON braces and all — was rendered straight into the alert. Even when
 *      the API sent a perfectly good sentence, the user saw it wrapped in
 *      JSON punctuation.
 *   2. `PATCH /materials/487` had hit a Postgres unique-constraint violation
 *      (`unique_material_code`) that jobq-api swallowed into a generic 500,
 *      so there was no sentence to render in the first place.
 *
 * (2) is fixed in jobq-api by returning a 409 that names the conflict. This
 * module fixes (1), and additionally translates the raw constraint text so a
 * bare Postgres error still reads as English if any endpoint is ever missed —
 * or if the UI is talking to an older API build mid-deploy.
 *
 * Three shapes of `detail` come back from FastAPI and all three must render:
 *   - string:  {"detail": "Material not found."}                 → the string
 *   - array:   {"detail": [{loc, msg, type}, ...]}  (422)        → field list
 *   - object:  {"detail": {"error": "...", "message": "..."}}    → .message
 *
 * The array case is why this is not just `JSON.parse(body).detail`: a 422
 * detail is an array, and calling `.includes()` on it throws, which is exactly
 * how a validation error ends up displayed as raw JSON.
 */

const GENERIC_500_DETAILS = new Set([
  "An internal server error occurred.",
  "Internal Server Error",
  "Internal server error",
]);

/**
 * Render FastAPI's 422 validation payload as a readable field list.
 * `loc` is like ["body", "width"] — the first element is the request part and
 * carries no meaning for the user, so it is dropped.
 */
function formatValidationErrors(items) {
  const parts = items
    .map((item) => {
      if (!item || typeof item !== "object") return String(item || "").trim();
      const loc = Array.isArray(item.loc) ? item.loc.slice(1) : [];
      const field = loc
        .filter((seg) => seg !== null && seg !== undefined)
        .join(" → ")
        .replace(/_/g, " ");
      const msg = (item.msg || item.type || "is invalid").trim();
      return field ? `${field}: ${msg}` : msg;
    })
    .filter(Boolean);

  if (!parts.length) return "";
  if (parts.length === 1) return `Please check this field — ${parts[0]}.`;
  return `Please check these fields — ${parts.join("; ")}.`;
}

/**
 * Collapse any `detail` shape into a plain string. Returns "" when there is
 * nothing usable, so callers can fall back to a status-based message.
 */
export function detailToString(detail) {
  if (detail === null || detail === undefined) return "";
  if (typeof detail === "string") return detail.trim();
  if (Array.isArray(detail)) return formatValidationErrors(detail);
  if (typeof detail === "object") {
    // An empty object says nothing: never render it as "{}".
    if (Object.keys(detail).length === 0) return "";
    // Structured errors the API raises deliberately, e.g. the transfer flow's
    // {"error": "source_mtn_id_required", "message": "..."}.
    if (typeof detail.message === "string") return detail.message.trim();
    if (typeof detail.detail === "string") return detail.detail.trim();
    try {
      return JSON.stringify(detail);
    } catch {
      return "";
    }
  }
  return String(detail).trim();
}

/**
 * Translate raw Postgres constraint text into English.
 * Returns null when the string is not a recognised database error, so the
 * caller can pass the API's own wording through untouched.
 */
function humanizeDatabaseError(detail) {
  if (!detail) return null;

  // Unique violations. Postgres puts the offending value in a DETAIL line:
  //   Key (material_code)=(12GA-CSB-GAVL-60x120-KCMFG) already exists.
  if (detail.includes("duplicate key") || detail.includes("already exists")) {
    const keyMatch = detail.match(/Key \(([^)]+)\)=\(([^)]+)\)/);
    if (keyMatch) {
      const column = keyMatch[1].replace(/_/g, " ");
      const value = keyMatch[2];
      return `A record with ${column} "${value}" already exists. Change the value, or edit the existing record instead of creating a second one.`;
    }
    if (detail.includes("unique_material_code")) {
      return "Another material already uses this material code. Material codes are generated from type, thickness, finish, width, length and owner — change one of those, or edit the existing material instead.";
    }
    if (detail.includes("duplicate key")) {
      return "That value is already used by another record. Please choose a different one.";
    }
  }

  if (detail.includes("foreign key") || detail.includes("violates foreign key")) {
    for (const [needle, label] of [
      ["customer_id", "Customer"],
      ["thickness_id", "Thickness"],
      ["material_id", "Material"],
      ["vendor_id", "Vendor"],
      ["supplier_id", "Supplier"],
    ]) {
      if (detail.includes(needle)) {
        return `The selected ${label} is invalid or no longer exists. Refresh the page and try again.`;
      }
    }
    // Postgres names TWO tables here, and the useful one is second:
    //   update or delete on table "materials" violates foreign key constraint
    //   "job_parts_material_id_fkey" on table "job_parts"
    // The first is the table being changed (which the user already knows); the
    // second is the table blocking them. A plain first-match regex reports the
    // wrong one, so prefer the DETAIL line and otherwise take the LAST match.
    const referencedFrom = detail.match(/referenced from table "([^"]+)"/);
    const allTables = [...detail.matchAll(/on table "([^"]+)"/g)];
    const blocking =
      (referencedFrom && referencedFrom[1]) ||
      (allTables.length ? allTables[allTables.length - 1][1] : null);
    if (blocking) {
      return `This record is still referenced by ${blocking.replace(/_/g, " ")}, so it cannot be changed or removed.`;
    }
    return "A referenced record is invalid or no longer exists. Refresh the page and try again.";
  }

  if (detail.includes("null value") && detail.includes("violates not-null")) {
    const fieldMatch = detail.match(/column "([^"]+)"/);
    if (fieldMatch) {
      const field = fieldMatch[1]
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
      return `${field} is required and cannot be empty.`;
    }
    return "A required field is missing. Please fill in every required field.";
  }

  if (detail.includes("numeric field overflow") || detail.includes("value too long")) {
    return "One of the values is too large or too long for the field it is saved in.";
  }

  return null;
}

/**
 * Fallback wording keyed off the HTTP status, for when the body carries no
 * usable detail (empty body, an API Gateway HTML page, a Lambda timeout).
 */
function messageForStatus(status, context) {
  const where = context ? `${context}: ` : "";
  switch (status) {
    case 400:
      return `${where}The server rejected the request as invalid.`;
    case 401:
    case 403:
      return `${where}You are not authorized to do that.`;
    case 404:
      return `${where}That record was not found. It may have been deleted — try refreshing.`;
    case 409:
      return `${where}That change conflicts with a record that already exists.`;
    case 413:
      return `${where}The upload is too large for the server to accept.`;
    case 422:
      return `${where}Some fields are missing or invalid.`;
    case 429:
      return `${where}Too many requests in a row. Wait a moment and try again.`;
    case 502:
    case 503:
    case 504:
      return `${where}The server did not respond in time. It may still be starting up — wait a few seconds and try again.`;
    case 500:
      return `${where}The server hit an unexpected error (HTTP 500). The cause is recorded in the API logs.`;
    default:
      return `${where}The request failed (HTTP ${status}).`;
  }
}

/**
 * Convert an error message string — typically a raw HTTP response body, or the
 * `.message` of a caught exception — into something worth showing a user.
 *
 * @param {string} errorMessage  Raw body or exception message.
 * @param {string} [context]     What the user was doing, e.g. "Failed to save material".
 * @param {number} [status]      HTTP status, when known. Used for the fallback wording.
 * @returns {string}
 */
export function parseApiError(errorMessage, context = "", status = null) {
  const raw = typeof errorMessage === "string" ? errorMessage.trim() : "";

  let detail = "";
  let isJson = false;
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      isJson = true;
      detail =
        parsed && typeof parsed === "object" && "detail" in parsed
          ? detailToString(parsed.detail)
          : detailToString(parsed);
    } catch {
      // Not JSON. A gateway or proxy failure arrives as an HTML error page —
      // dumping that into the banner is exactly the raw-body problem this
      // module exists to stop, so only keep a body that reads like a sentence.
      const looksLikeMarkup = /^\s*[<{[]/.test(raw) || /<\/?[a-z][\s\S]*>/i.test(raw);
      detail = looksLikeMarkup || raw.length > 300 ? "" : raw;
    }
  }

  // Network-level failures never carry a status; fetch() rejects outright.
  if (!status) {
    // Chrome says "Failed to fetch"; Safari (every iPhone) says "Load failed".
    if (raw.includes("Failed to fetch") || raw === "Load failed" || raw === "TypeError: Load failed") {
      return "Unable to reach the server. Check your internet connection and try again. (An oversized upload can also look like this.)";
    }
    if (raw.includes("NetworkError") || raw.includes("Network request failed")) {
      return "A network error occurred. Check your connection and try again.";
    }
    if (/timeout/i.test(raw)) {
      return "The request timed out. Please try again.";
    }
  }

  // The API's sanitized 500 tells the user nothing; say what to do instead.
  if (GENERIC_500_DETAILS.has(detail)) {
    const where = context ? `${context}. ` : "";
    return `${where}The server rejected the change and did not say why (HTTP 500). This is usually a duplicate code, a missing required field, or a Customer/Material/Thickness that no longer exists. The exact cause is in the API logs.`;
  }

  const humanized = humanizeDatabaseError(detail);
  if (humanized) return context ? `${context}: ${humanized}` : humanized;

  if (detail) {
    // Don't double up when the API's own message already says what failed.
    if (!context) return detail;
    const lower = detail.toLowerCase();
    if (lower.includes(context.toLowerCase())) return detail;
    return `${context}: ${detail}`;
  }

  if (status) return messageForStatus(status, context);
  // A JSON body with no usable detail ({}, null, []) is not a sentence: never echo it.
  return (isJson ? "" : raw) || (context ? `${context}: the request failed.` : "The request failed.");
}

/**
 * Read a non-ok `Response` and return a friendly message. Never throws, and
 * never leaves the body unread.
 *
 * Usage:
 *   if (!res.ok) throw new Error(await apiErrorMessage(res, "Failed to save material"));
 *
 * @param {Response} res
 * @param {string} [context]
 * @returns {Promise<string>}
 */
export async function apiErrorMessage(res, context = "") {
  let body = "";
  try {
    body = await res.text();
  } catch {
    // Body already consumed or the stream broke — fall back to the status.
    body = "";
  }
  return parseApiError(body, context, res?.status ?? null);
}

/**
 * The friendly message for a failed axios call (err.response = {status, data}). The same rules as
 * parseApiError: a plain-sentence detail is kept (prefixed with the context), the sanitized 500
 * and database errors are turned into guidance. No response at all (a network error) → context.
 *
 * Usage:
 *   catch (err) { showMessage(axiosErrorMessage(err, "Failed to create invoice"), "error"); }
 */
export function axiosErrorMessage(err, context = "") {
  const r = err && err.response;
  if (!r) return context || "The request failed.";
  const raw = typeof r.data === "string" ? r.data : JSON.stringify(r.data || {});
  return parseApiError(raw, context, r.status);
}

export default parseApiError;
